import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { v4 as uuidv4 } from 'uuid';

import { APP_CONFIG } from '../../environment';
import { Env } from '../../environment/env';
import { HttpProvider } from '../http/http';
import { RouteProvider } from '../route/route';
import { LoadingProvider } from '../loading/loading';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { OrderProvider } from '../order/order';
import { ITrueLayerFailure, TrueLayerPlugin } from '../truelayer/truelayer-sdk';

import { IOrder } from '../../model/models/order/order-interface';
import { ErrorCodeType } from '../../model/types/error-code-type';
import {
  IOpenBankingInitiateRequest,
  IOpenBankingInitiateResponse,
} from '../../model/api/openbanking/openbanking-initiate.entity.interface';
import {
  IOpenBankingStatusRequest,
  IOpenBankingStatusResponse,
} from '../../model/api/openbanking/openbanking-status.entity.interface';

@Injectable()
export class SendOpenBankingProvider {

  private static readonly OPEN_BANKING_PROVIDER_ID = 12090;
  private static readonly STATUS_POLL_TIMEOUT_MS = 15000;
  private static readonly STATUS_POLL_INTERVAL_MS = 3000;

  private order: IOrder | null = null;

  constructor(
    private httpProvider: HttpProvider,
    private routeProvider: RouteProvider,
    private loadingProvider: LoadingProvider,
    private platform: Platform,
    private trueLayerPlugin: TrueLayerPlugin,
    private orderProvider: OrderProvider,
  ) {}

  public async initOpenBanking(order: IOrder): Promise<void> {
    this.order = order;
    if (!order || !order.transactionId || !order.orderNumber) {
      LoggerProvider.error(this, LoggerEvent.ProviderException,
        'SendOpenBankingProvider: initOpenBanking called without a valid order (missing transactionId/orderNumber)');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
      return;
    }
    await this.initiate(order);
  }

  private async initiate(order: IOrder): Promise<void> {
    const body: IOpenBankingInitiateRequest = {
      correlation_id: uuidv4(),
      TransactionId: order.transactionId!,
      OrderNo: order.orderNumber!
    };

    LoggerProvider.info(this, 'POST /bank/openbanking/initiate: ' + JSON.stringify(body));

    this.loadingProvider.show();
    let initRes: IOpenBankingInitiateResponse;
    try {
      initRes = await this.httpProvider.post(
        `${APP_CONFIG.remittanceApiBaseUrl}bank/openbanking/initiate`,
        body,
      );

      LoggerProvider.info(this, 'openbanking/initiate response: ' + JSON.stringify(initRes));

      if (!initRes || !initRes.id || !initRes.widget_token) {
        LoggerProvider.error(this, LoggerEvent.ProviderException,
          'openbanking/initiate response missing id or widget_token');
        await this.cancelOrder('Open banking initiate failed');
        this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
          isTrueLayerError: true,
          errorCode: (initRes && initRes.errorCode) || ErrorCodeType.ApmGenericFailure113,
          error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
        } as any);
        return;
      }
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'openbanking/initiate failed');
      await this.cancelOrder('Open banking initiate failed');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
      return;
    } finally {
      this.loadingProvider.hide();
    }

    await this.launchTrueLayerSdk(initRes);
  }

  private async launchTrueLayerSdk(init: IOpenBankingInitiateResponse): Promise<void> {
    if (!this.platform.is('cordova')) {
      LoggerProvider.error(this, LoggerEvent.ProviderException,
        'TrueLayer SDK skipped: platform is not Cordova (running in browser?)');
      await this.cancelOrder('Open banking SDK unavailable');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
      return;
    }
    try {
      await this.trueLayerPlugin.configure({ environment: APP_CONFIG.env === Env.PROD ? 'production' : 'sandbox' });
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'TrueLayer SDK configure failed');
      await this.cancelOrder('Open banking SDK configure failed');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
      return;
    }
    try {
      const sdkRes = await this.trueLayerPlugin.processSinglePayment({
        paymentId: init.id,
        resourceToken: init.widget_token,
        redirectUri: 'riamoneytransfer://truelayer-redirect',
      });
      LoggerProvider.info(this, 'TrueLayer SDK result: ' + JSON.stringify(sdkRes));
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'TrueLayer open-banking SDK handoff failed');
      const reason = (err && (err as ITrueLayerFailure).reason) || 'unknown';
      await this.cancelOrderIfPaymentNotActive(init, `Open banking SDK error: ${reason}`);
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
      return;
    }

    await this.checkStatusAndNavigate(init);
  }

  private async checkStatusAndNavigate(init: IOpenBankingInitiateResponse): Promise<void> {
    // bank/openbanking/status returns one of three statuses: Success, Progress, Error
    const deadline = Date.now() + SendOpenBankingProvider.STATUS_POLL_TIMEOUT_MS;

    this.loadingProvider.show();
    try {
      let statusRes: IOpenBankingStatusResponse;
      let status: string;
      let attemptedOnce = false;

      do {
        if (attemptedOnce)
          await this.delay(SendOpenBankingProvider.STATUS_POLL_INTERVAL_MS);
        attemptedOnce = true;

        statusRes = await this.fetchOpenBankingStatus(init);
        status = this.normalizeStatus(statusRes);
      } while (status === 'Progress' && Date.now() < deadline);

      if (status === 'Success' || status === 'Progress') {
        this.routeProvider.push(this.routeProvider.pages.SendCompletedPage, { reviewPrompt: false } as any);
        return;
      }

      if (status)
        await this.cancelOrder(`Open banking payment status: ${status}`);
      else
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'openbanking/status returned no status, not cancelling');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: (statusRes && statusRes.errorCode) || ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'openbanking/status check failed');
      this.routeProvider.push(this.routeProvider.pages.SendFailedPage, {
        isTrueLayerError: true,
        errorCode: ErrorCodeType.ApmGenericFailure113,
        error: { errorCode: ErrorCodeType.ApmAuthorizationFailed },
      } as any);
    } finally {
      this.loadingProvider.hide();
    }
  }

  private async fetchOpenBankingStatus(init: IOpenBankingInitiateResponse): Promise<IOpenBankingStatusResponse> {
    const body: IOpenBankingStatusRequest = {
      correlation_id: uuidv4(),
      TransactionId: this.order!.transactionId!,
      OrderNo: this.order!.orderNumber!,
      ExtTransactionId: init.id,
    };
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}bank/openbanking/status`, body);
  }

  private normalizeStatus(statusRes: IOpenBankingStatusResponse): string {
    const rawStatus = (statusRes && statusRes.status) || '';
    return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
  }

  private async cancelOrderIfPaymentNotActive(init: IOpenBankingInitiateResponse, cancelReason: string): Promise<void> {
    if (!this.order || !this.order.orderNumber)
      return;
    let status: string;
    this.loadingProvider.show();
    try {
      status = this.normalizeStatus(await this.fetchOpenBankingStatus(init));
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'openbanking/status failed after SDK error, not cancelling');
      return;
    } finally {
      this.loadingProvider.hide();
    }
    if (!status || status === 'Success' || status === 'Progress') {
      LoggerProvider.info(this, `Order ${this.order.orderNumber} payment status '${status}' after SDK error, not cancelling`);
      return;
    }
    await this.cancelOrder(`${cancelReason} (status: ${status})`);
  }

  private async cancelOrder(cancelReason: string): Promise<void> {
    if (!this.order || !this.order.orderNumber)
      return;
    this.loadingProvider.show();
    try {
      await this.orderProvider.cancelOrder({ orderNumber: this.order.orderNumber, cancelReason });
      LoggerProvider.info(this, `Order ${this.order.orderNumber} cancelled: ${cancelReason}`);
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'order/cancel failed');
    } finally {
      this.loadingProvider.hide();
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }
}



// WEBPACK FOOTER //
// ./src/providers/openbanking/send-openbanking.ts